import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { FileBarChart, Send, History, Settings, Eye, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWeeklyReports() {
  const { authFetch } = useAdmin();
  const [tab, setTab] = useState('preview');
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, day: 'tuesday', time: '09:00', email: '', days_range: 7 });
  const [busy, setBusy] = useState(false);
  const [sendTo, setSendTo] = useState('');

  const load = useCallback(async () => {
    const [h, s] = await Promise.all([
      authFetch(`${API}/admin/reports/weekly/history`),
      authFetch(`${API}/admin/reports/weekly/settings`),
    ]);
    setHistory((await h.json()).reports || []);
    const cfg = await s.json();
    setSettings(cfg);
    setSendTo(cfg.email || '');
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function generate() {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/admin/reports/weekly/generate?days=${settings.days_range}`, { method: 'POST' });
      setPreview(await r.json());
    } finally { setBusy(false); }
  }

  async function send() {
    if (!sendTo) { toast.error('Unesi email'); return; }
    setBusy(true);
    const r = await authFetch(`${API}/admin/reports/weekly/send`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: sendTo, days: settings.days_range }),
    });
    setBusy(false);
    const d = await r.json();
    if (d.ok) toast.success(d.mocked ? 'Poslano (MOCK)' : 'Email poslan'); else toast.error('Greška');
    load();
  }

  async function saveSettings() {
    const r = await authFetch(`${API}/admin/reports/weekly/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings),
    });
    if (r.ok) toast.success('Postavke spremljene');
  }

  const inp = "w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:border-red-500 outline-none";

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-weekly-reports-page">
      <header className="mb-6">
        <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><FileBarChart size={28} className="text-red-500" /> Tjedni izvještaji</h1>
        <p className="text-xs text-zinc-500 mt-1">Agregirana statistika + HTML email putem SendGrid-a.</p>
      </header>

      <div className="flex gap-1 mb-5 border-b border-zinc-800">
        {[['preview', <Eye size={14} />, 'Pregled'], ['history', <History size={14} />, 'Povijest'], ['settings', <Settings size={14} />, 'Postavke']].map(([k, i, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 ${tab === k ? 'border-red-500 text-zinc-100' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`} data-testid={`tab-${k}`}>
            {i} {l}
          </button>
        ))}
      </div>

      {tab === 'preview' && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            <button onClick={generate} disabled={busy} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="generate-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Generiraj pregled
            </button>
            <input type="email" placeholder="primatelj@e-mina.eu" value={sendTo} onChange={e => setSendTo(e.target.value)} className={`${inp} max-w-xs`} data-testid="send-to-input" />
            <button onClick={send} disabled={busy || !sendTo} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="send-btn">
              <Send size={14} /> Pošalji
            </button>
          </div>
          {preview && (
            <div className="bg-white rounded-lg overflow-hidden border border-zinc-800" data-testid="preview-iframe-wrap">
              <iframe title="Pregled" srcDoc={preview.html} className="w-full h-[600px] bg-white" />
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-2" data-testid="history-list">
          {history.length === 0 ? <p className="text-zinc-500 text-center py-10">Nema izvještaja.</p> :
            history.map(h => (
              <div key={h.id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-zinc-100">{new Date(h.created_at).toLocaleString('hr')}</div>
                  <div className="text-xs text-zinc-500">Razdoblje: {h.report?.period_days || 7} dana · {h.report?.published_count} objavljeno · {(h.report?.total_views || 0).toLocaleString('hr')} pregleda</div>
                </div>
                <div className="text-xs">
                  {h.sent_to ? (
                    <span className={`px-2 py-0.5 rounded ${h.sent_success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {h.sent_success ? '✓ Poslano' : '✗ Greška'} → {h.sent_to}
                    </span>
                  ) : <span className="text-zinc-500">Samo pregled</span>}
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === 'settings' && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6 space-y-4 max-w-2xl">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={settings.enabled} onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))} />
            Automatsko slanje omogućeno
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block"><span className="text-xs text-zinc-400 block mb-1">Dan u tjednu</span>
              <select className={inp} value={settings.day} onChange={e => setSettings(s => ({ ...s, day: e.target.value }))}>
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label className="block"><span className="text-xs text-zinc-400 block mb-1">Vrijeme (UTC)</span>
              <input type="time" className={inp} value={settings.time} onChange={e => setSettings(s => ({ ...s, time: e.target.value }))} />
            </label>
            <label className="block"><span className="text-xs text-zinc-400 block mb-1">Raspon (dana)</span>
              <input type="number" className={inp} value={settings.days_range} onChange={e => setSettings(s => ({ ...s, days_range: +e.target.value }))} />
            </label>
            <label className="block md:col-span-2"><span className="text-xs text-zinc-400 block mb-1">Email primatelj</span>
              <input type="email" className={inp} value={settings.email} onChange={e => setSettings(s => ({ ...s, email: e.target.value }))} placeholder="admin@e-mina.eu" />
            </label>
          </div>
          <button onClick={saveSettings} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md" data-testid="settings-save-btn">Spremi postavke</button>
        </div>
      )}
    </div>
  );
}
